import CandidateScheduleDialog from "../CandidateScheduleDialog";
import { createAgentVoice } from "../../utils/agentVoice";
import CompanyTableCell from "../CompanyTableCell";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TableCell,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SearchOutlined as SearchOutlinedIcon } from "../icons/FigmaControlIcons";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import MicOffOutlinedIcon from "@mui/icons-material/MicOffOutlined";
import { BoltOutlined as BoltOutlinedIcon } from "../icons/FigmaControlIcons";
import AddIcon from "@mui/icons-material/Add";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { EditOutlined as EditOutlinedIcon } from "../icons/FigmaControlIcons";
import { DeleteOutline as DeleteOutlineIcon } from "../icons/FigmaControlIcons";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import AiRpoLogo from "../../assets/ai-rpo-logo.svg";
import GenericDataTable from "../GenericDataTable";
import { formatLocalDate, formatLocalDateTime, toTimeValue } from "../../utils/dateTime";
import { writeAiAgentWorkState } from "../../utils/aiAgentWorkState";

const API_BASE = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const DEFAULT_LOGO = AiRpoLogo;
const MAX_ATTACHMENTS = 5;
const JOBS_UPDATED_EVENT = "rpo:jobs-updated";
const DEFAULT_ATTACHMENT_PROMPT =
  "Summarize the attached file(s) and pull out the most useful recruiting insights.";

const safeTrim = (value) => (value === undefined || value === null ? "" : String(value).trim());
const buildApiUrl = (pathname) => `${API_BASE}${pathname.startsWith("/") ? pathname : `/${pathname}`}`;
const toPositiveId = (value) => {
  const normalized = Number(value || 0);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : 0;
};
const RESULT_LINK_STYLES = {
  job: { bgcolor: "rgba(124,58,237,0.10)", color: "#6D28D9", borderColor: "rgba(124,58,237,0.25)" },
  candidate: { bgcolor: "rgba(14,165,233,0.12)", color: "#0F766E", borderColor: "rgba(14,165,233,0.22)" },
  client: { bgcolor: "rgba(59,130,246,0.10)", color: "#1D4ED8", borderColor: "rgba(59,130,246,0.22)" },
  company: { bgcolor: "rgba(16,185,129,0.10)", color: "#047857", borderColor: "rgba(16,185,129,0.22)" },
  activity: { bgcolor: "rgba(244,114,182,0.12)", color: "#BE185D", borderColor: "rgba(244,114,182,0.24)" },
  sequence: { bgcolor: "rgba(245,158,11,0.12)", color: "#B45309", borderColor: "rgba(245,158,11,0.24)" },
  content: { bgcolor: "rgba(6,182,212,0.10)", color: "#0F766E", borderColor: "rgba(6,182,212,0.22)" },
  result: { bgcolor: "rgba(148,163,184,0.12)", color: "#475569", borderColor: "rgba(148,163,184,0.22)" },
  external: { bgcolor: "rgba(251,191,36,0.14)", color: "#92400E", borderColor: "rgba(245,158,11,0.24)" },
};

function getAuthToken() {
  if (typeof window === "undefined") return "";
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    ""
  );
}

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function formatTimestamp(value) {
  return formatLocalDateTime(value, {}, "");
}

function resolveImportedJobId(payload = {}) {
  const groups = [
    payload?.summary?.jobs?.created,
    payload?.summary?.jobs?.updated,
    payload?.summary?.jobs?.existing,
  ];

  for (const group of groups) {
    const match = (Array.isArray(group) ? group : [])
      .map((entry) => toPositiveId(entry?.id))
      .find((value) => value > 0);
    if (match) return match;
  }

  return (
    (Array.isArray(payload?.imported_job_ids) ? payload.imported_job_ids : [])
      .map((value) => toPositiveId(value))
      .find((value) => value > 0) || 0
  );
}

const PromptComposer = forwardRef(function PromptComposer(
  { autoFocus = false, disabled, isListening, onSubmit, onToggleMic },
  ref
) {
  const [draft, setDraft] = useState("");
  const draftRef = useRef("");

  useImperativeHandle(
    ref,
    () => ({
      getValue: () => draftRef.current,
      setValue: (nextValue = "") => {
        const normalized = String(nextValue ?? "");
        draftRef.current = normalized;
        setDraft(normalized);
      },
      clear: () => {
        draftRef.current = "";
        setDraft("");
      },
      appendText: (nextValue = "") => {
        const normalized = safeTrim(nextValue);
        if (!normalized) return;
        const nextDraft = `${draftRef.current}${
          draftRef.current && !draftRef.current.endsWith(" ") ? " " : ""
        }${normalized}`.trimStart();
        draftRef.current = nextDraft;
        setDraft(nextDraft);
      },
    }),
    []
  );

  return (
    <TextField
      fullWidth
      autoFocus={autoFocus}
      autoComplete="off"
      name="ai-agent-prompt"
      value={draft}
      onChange={(event) => {
        const nextDraft = event.target.value;
        draftRef.current = nextDraft;
        setDraft(nextDraft);
      }}
      placeholder={isListening ? "Listening..." : "Ask anything"}
      onKeyDown={(event) => {
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          onSubmit?.();
        }
      }}
      disabled={disabled}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: "10px",
          bgcolor: "#fff",
          "& fieldset": { borderColor: "rgba(124,58,237,0.35)" },
          "&:hover fieldset": { borderColor: "rgba(124,58,237,0.7)" },
          "&.Mui-focused fieldset": {
            borderColor: "rgba(124,58,237,1)",
            borderWidth: "1px",
          },
        },
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={onSubmit}
              aria-label="Send message"
              disabled={disabled || isListening}
              sx={{ width: 44, height: 44, borderRadius: "10px", color: "#7C3AED" }}
            >
              <SendRoundedIcon />
            </IconButton>
            <IconButton
              aria-label={isListening ? "Stop recording" : "Start voice input"}
              aria-pressed={isListening}
              onClick={onToggleMic}
              disabled={disabled}
              sx={{
                width: 44,
                height: 44,
                borderRadius: "10px",
                color: isListening ? "#EF4444" : "#7C3AED",
              }}
            >
              {isListening ? <MicOffOutlinedIcon /> : <MicNoneOutlinedIcon />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      inputProps={{
        autoComplete: "off",
        autoCorrect: "off",
        autoCapitalize: "none",
        spellCheck: false,
      }}
    />
  );
});

function normalizeResultLink(link = {}, index = 0) {
  const href = safeTrim(link?.href || link?.url);
  const label = safeTrim(link?.label || link?.title);
  if (!href || !label) return null;

  return {
    id: safeTrim(link?.id) || `result-link-${index + 1}`,
    kind: safeTrim(link?.kind || link?.type).toLowerCase() || "result",
    label,
    href,
    pageLabel: safeTrim(link?.page_label || link?.pageLabel),
    external: Boolean(link?.external) || /^https?:\/\//i.test(href),
  };
}

function getResultLinkStyle(link = {}) {
  if (link?.external) return RESULT_LINK_STYLES.external;
  return RESULT_LINK_STYLES[link?.kind] || RESULT_LINK_STYLES.result;
}

const JOBSPY_TABLE_COLUMNS = [
  { id: "title", label: "Job" },
  { id: "company", label: "Company Name" },
  { id: "location", label: "Location" },
  { id: "date_posted_sort", label: "Posted" },
  { id: "site", label: "Source" },
  { id: "status_sort", label: "Status" },
];

const JOBSPY_STATUS_META = {
  created: {
    label: "Saved",
    sx: { bgcolor: "rgba(16,185,129,0.12)", color: "#047857", borderColor: "rgba(16,185,129,0.24)" },
    order: 3,
  },
  updated: {
    label: "Updated",
    sx: { bgcolor: "rgba(59,130,246,0.10)", color: "#1D4ED8", borderColor: "rgba(59,130,246,0.24)" },
    order: 2,
  },
  existing: {
    label: "Already in DB",
    sx: { bgcolor: "rgba(251,191,36,0.14)", color: "#92400E", borderColor: "rgba(245,158,11,0.24)" },
    order: 1,
  },
  skipped: {
    label: "Not saved",
    sx: { bgcolor: "rgba(239,68,68,0.10)", color: "#B91C1C", borderColor: "rgba(239,68,68,0.22)" },
    order: 4,
  },
  pending: {
    label: "Ready",
    sx: { bgcolor: "rgba(148,163,184,0.12)", color: "#475569", borderColor: "rgba(148,163,184,0.22)" },
    order: 0,
  },
};

function formatJobSpyPosted(value) {
  const raw = safeTrim(value);
  if (!raw) return "";
  return formatLocalDate(raw, {}, raw);
}

function getJobSpyPostedSortValue(value) {
  return toTimeValue(value);
}

function getJobSpyStatusMeta(status) {
  return JOBSPY_STATUS_META[safeTrim(status).toLowerCase()] || JOBSPY_STATUS_META.pending;
}

function cleanJobSpySummaryText(value) {
  return replaceOutsideInlineCode(safeTrim(value), transformAiAgentTextSegment)
    .replace(/^##\s+/gm, "")
    .trim();
}

function replaceOutsideInlineCode(text = "", transform = (value) => value) {
  return String(text || "")
    .split(/(```[\s\S]*?```|`[^`\n]+`)/g)
    .map((part, index) => (index % 2 === 1 ? part : transform(part)))
    .join("");
}

function transformAiAgentTextSegment(value = "") {
  return String(value || "")
    .replace(
      /\bImported by RPO AI Agent via JobSpy from\b/gi,
      "Imported by RPO AI Agent from"
    )
    .replace(
      /\b(Python (?:package|dependency)|module)\s+(['"])jobspy\2/gi,
      (_match, prefix, quote) => `${prefix} ${quote}jobspy${quote}`
    )
    .replace(
      /\b(Python (?:package|dependency)|module)\s+jobspy\b/gi,
      (_match, prefix) => `${prefix} jobspy`
    )
    .replace(/\busing the provided JobSpy\/Indeed results\b/gi, "using the provided external job board results")
    .replace(/\bprovided JobSpy\/Indeed results\b/gi, "provided external job board results")
    .replace(/\bExternal job board findings\s*\(JobSpy\)\s*:?/gi, "External job board findings:")
    .replace(/\bFailed to start JobSpy\b/gi, "Failed to start the external job board search")
    .replace(/\bJobSpy failed to start\b/gi, "External job board search failed to start")
    .replace(/\bJobSpy timed out after\b/gi, "External job board search timed out after")
    .replace(/\bJobSpy exited with code\b/gi, "External job board search exited with code")
    .replace(/\bJobSpy returned an invalid response\b/gi, "External job board search returned an invalid response")
    .replace(/\bJobSpy search failed\b/gi, "External job board search failed")
    .replace(/\bJobSpy results\b/gi, "external job board results")
    .replace(/\bJobSpy rows\b/gi, "search results")
    .replace(/\bvia JobSpy\b/gi, "")
    .replace(/\bJOBSPY\b/g, "external job board search")
    .replace(/\bJobSpy\b/g, "external job board search");
}

function escapeRegExp(value = "") {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function isAiAgentSectionHeading(line = "", title = "") {
  const trimmed = safeTrim(line);
  if (!trimmed || !title) return false;

  const pattern = new RegExp(
    `^(?:(?:#{1,6}\\s*|\\*\\*\\s*)?)${escapeRegExp(title)}(?:\\s*\\*\\*)?\\s*:?$`,
    "i"
  );

  return pattern.test(trimmed);
}

function stripAiAgentSection(text = "", title = "") {
  const lines = String(text || "").split("\n");
  if (!lines.length || !title) return safeTrim(text);

  const nextSectionPattern = /^(?:#{1,6}\s+.+|\*\*[^*]+(?:\*\*)?\s*:?)$/i;
  const output = [];
  let skipping = false;

  lines.forEach((line) => {
    if (!skipping && isAiAgentSectionHeading(line, title)) {
      skipping = true;
      return;
    }

    if (skipping) {
      const trimmed = safeTrim(line);
      if (
        trimmed &&
        (isAiAgentSectionHeading(line, "Recommended next steps") ||
          isAiAgentSectionHeading(line, "Internal database results") ||
          nextSectionPattern.test(trimmed))
      ) {
        skipping = false;
      } else {
        return;
      }
    }

    output.push(line);
  });

  return output.join("\n");
}

function keepVisibleResultLinks(links = []) {
  return (Array.isArray(links) ? links : [])
    .map((link, index) => normalizeResultLink(link, index))
    .filter(Boolean);
}

function keepVisibleCitations(citations = [], jobSpyResults = []) {
  const jobSpyUrlSet = new Set(
    (Array.isArray(jobSpyResults) ? jobSpyResults : [])
      .map((job) => safeTrim(job?.job_url))
      .filter(Boolean)
      .map((url) => url.toLowerCase())
  );

  return (Array.isArray(citations) ? citations : []).filter((citation) => {
    const url = safeTrim(citation?.url).toLowerCase();
    if (!url) return false;
    return !jobSpyUrlSet.has(url);
  });
}

function cleanAiAgentMessageText(value, { hideExternalWebFindings = false } = {}) {
  let text = safeTrim(value).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  if (!text) return "";

  text = replaceOutsideInlineCode(text, transformAiAgentTextSegment);
  if (hideExternalWebFindings) {
    text = stripAiAgentSection(text, "External web findings");
  }

  return text
    .replace(/\(\s*\)/g, "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function normalizeMessageItem(item = {}) {
  const jobSpyResults = (
    Array.isArray(item?.jobspy_results)
      ? item.jobspy_results
      : Array.isArray(item?.jobSpyResults)
        ? item.jobSpyResults
        : []
  ).map((job, index) => ({
    ...job,
    id: safeTrim(job?.id) || `jobspy-${index + 1}`,
  }));
  const role = safeTrim(item?.role) || "user";
  const mode = safeTrim(item?.mode).toLowerCase() === "search" ? "search" : "chat";
  const cleanedText = cleanAiAgentMessageText(item?.text, {
    hideExternalWebFindings: jobSpyResults.length > 0,
  });
  const shouldHideText = role === "assistant" && mode === "search" && jobSpyResults.length > 0;

  return {
    id: item?.id ?? `${item?.role || "message"}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    role,
    text: shouldHideText ? "" : cleanedText,
    mode,
    attachments: Array.isArray(item?.attachments) ? item.attachments : [],
    citations: keepVisibleCitations(item?.citations, jobSpyResults),
    jobSpyResults,
    resultLinks: keepVisibleResultLinks(
      Array.isArray(item?.result_links)
        ? item.result_links
        : Array.isArray(item?.resultLinks)
          ? item.resultLinks
          : []
    ),
    responseId: safeTrim(item?.response_id || item?.responseId),
    model: safeTrim(item?.model),
    createdAt: item?.created_at || item?.createdAt || new Date().toISOString(),
  };
}

function hasRenderableMessageContent(item = {}) {
  if (safeTrim(item?.text)) return true;
  if (Array.isArray(item?.jobSpyResults) && item.jobSpyResults.length) return true;
  if (Array.isArray(item?.resultLinks) && item.resultLinks.length) return true;
  if (Array.isArray(item?.attachments) && item.attachments.length) return true;
  return false;
}

function mergeAssistantPayloadWithResponse(payload = {}, fallbackMode = "chat") {
  const assistantMessage =
    payload?.assistant_message && typeof payload.assistant_message === "object"
      ? payload.assistant_message
      : {};

  const assistantJobSpyResults = Array.isArray(assistantMessage?.jobspy_results)
    ? assistantMessage.jobspy_results
    : Array.isArray(assistantMessage?.jobSpyResults)
      ? assistantMessage.jobSpyResults
      : [];

  const assistantResultLinks = Array.isArray(assistantMessage?.result_links)
    ? assistantMessage.result_links
    : Array.isArray(assistantMessage?.resultLinks)
      ? assistantMessage.resultLinks
      : [];

  return {
    ...assistantMessage,
    role: safeTrim(assistantMessage?.role) || "assistant",
    text:
      assistantMessage?.text !== undefined
        ? assistantMessage.text
        : payload?.answer ?? "",
    mode: safeTrim(assistantMessage?.mode) || safeTrim(payload?.mode) || fallbackMode,
    citations: Array.isArray(assistantMessage?.citations)
      ? assistantMessage.citations
      : Array.isArray(payload?.citations)
        ? payload.citations
        : [],
    result_links: assistantResultLinks.length
      ? assistantResultLinks
      : Array.isArray(payload?.result_links)
        ? payload.result_links
        : [],
    jobspy_results: assistantJobSpyResults.length
      ? assistantJobSpyResults
      : Array.isArray(payload?.jobspy_results)
        ? payload.jobspy_results
        : [],
  };
}

function AIAgentJobSpyTable({
  messageId,
  jobs,
  selectedIds,
  onSelectedIdsChange,
  onSaveSelected,
  isSaving = false,
  feedback,
}) {
  const rows = (Array.isArray(jobs) ? jobs : []).map((job) => {
    const statusMeta = getJobSpyStatusMeta(job?.import_status);
    return {
      ...job,
      date_posted_sort: getJobSpyPostedSortValue(job?.date_posted),
      date_posted_label: formatJobSpyPosted(job?.date_posted),
      status_label: statusMeta.label,
      status_sort: statusMeta.order,
      status_sx: statusMeta.sx,
    };
  });

  const renderRow = (row) => (
    <>
      <TableCell sx={{ minWidth: 280 }}>
        <Stack spacing={0.5}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1F2937" }}>
            {safeTrim(row?.title) || "Untitled job"}
          </Typography>
          {!!safeTrim(row?.job_url) && (
            <Link
              href={row.job_url}
              target="_blank"
              rel="noreferrer"
              underline="hover"
              sx={{
                fontSize: 12,
                color: "#7C3AED",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.4,
                width: "fit-content",
              }}
            >
              Open posting
              <LaunchOutlinedIcon sx={{ fontSize: 14 }} />
            </Link>
          )}
        </Stack>
      </TableCell>
      <TableCell sx={{ minWidth: 150 }}><CompanyTableCell row={row} /></TableCell>
      <TableCell sx={{ minWidth: 160 }}>
        <Typography sx={{ fontSize: 13, color: "#374151" }}>
          {safeTrim(row?.location) || "Not listed"}
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 110 }}>
        <Typography sx={{ fontSize: 13, color: "#374151" }}>
          {row?.date_posted_label || "Unknown"}
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 120 }}>
        <Typography sx={{ fontSize: 13, color: "#374151" }}>
          {safeTrim(row?.site) || "Job board"}
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 180 }}>
        <Stack spacing={0.6}>
          <Chip
            size="small"
            label={row?.status_label || "Ready"}
            variant="outlined"
            sx={{ borderRadius: "999px", width: "fit-content", ...row?.status_sx }}
          />
          {!!safeTrim(row?.import_reason) && (
            <Typography sx={{ fontSize: 11, color: "#6B7280", lineHeight: 1.4 }}>
              {row.import_reason}
            </Typography>
          )}
        </Stack>
      </TableCell>
    </>
  );

  const selectedCount = Array.isArray(selectedIds) ? selectedIds.length : 0;
  const bulkActionsNode = (
    <Stack
      marginBottom={1}
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      justifyContent="space-between"
      alignItems={{ xs: "stretch", sm: "center" }}
    >
      <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
        {selectedCount
          ? `${selectedCount} search result${selectedCount === 1 ? "" : "s"} selected`
          : "Select the rows you want to save into the jobs table."}
      </Typography>
      <Button
        variant="contained"
        onClick={() => onSaveSelected?.(messageId)}
        disabled={isSaving || !selectedCount}
        sx={{
          textTransform: "none",
          borderRadius: "999px",
         
          background: "linear-gradient(135deg, #7C3AED, #A855F7)",
          "&:hover": { background: "linear-gradient(135deg, #6D28D9, #9333EA)" },
        }}
      >
        {isSaving ? "Saving..." : selectedCount ? `Save selected (${selectedCount})` : "Save selected"}
      </Button>
    </Stack>
  );

  return (
    <Stack spacing={1.1} sx={{ mt: 1.3 }}>
      <GenericDataTable
        columns={JOBSPY_TABLE_COLUMNS}
        rows={rows}
        renderRow={renderRow}
        defaultOrderBy="title"
        includeSelectionColumn={true}
        selectedIds={selectedIds}
        onSelectedIdsChange={onSelectedIdsChange}
        initialRowsPerPage={10}
        bulkActionsTop={bulkActionsNode}
        rootSx={{
          borderRadius: "16px",
          overflow: "hidden",
          bgcolor: "#FFFFFF",
          border: "1px solid rgba(148,163,184,0.18)",
        }}
        topBulkActionsWrapperSx={{
          px: 1.5,
          pt: 0,
          mb: 0,
          borderTop: "none",
        }}
        tableContainerSx={{
          borderTop: "1px solid rgba(226,232,240,0.9)",
          "& .MuiTableCell-root": {
            verticalAlign: "top",
          },
        }}
        paginationBarSx={{
          px: 1.5,
          mt: 2,
          mb: 1.2,
        }}
      />

      {!!safeTrim(feedback?.text) && (
        <Alert severity={feedback?.severity || "info"} sx={{ borderRadius: 2 }}>
          <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>
            {feedback.text}
          </Typography>
        </Alert>
      )}
    </Stack>
  );
}

export default function AIAgentDialog({
  open,
  onClose,
  user,
  logoUrl = DEFAULT_LOGO,
  onSend,
  onAttach,
  onSearch,
  onHistory,
  onActivity,
  initialPrompt = "",
  openAttachmentPickerOnOpen = false,
  submitOnOpen = false,
}) {
  const navigate = useNavigate();
  const [isListening, setIsListening] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [meetingDraft, setMeetingDraft] = useState(null);
  const [reviewMeeting, setReviewMeeting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [conversationItems, setConversationItems] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState("");
  const [editingValue, setEditingValue] = useState("");
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [jobSpySelectedIdsByMessage, setJobSpySelectedIdsByMessage] = useState({});
  const [jobSpySaveStateByMessage, setJobSpySaveStateByMessage] = useState({});

  const voiceRef = useRef(null);
  const fileInputRef = useRef(null);
  const composerRef = useRef(null);
  const endRef = useRef(null);
  const attachmentPickerAutoOpenedRef = useRef(false);
  const pendingSubmitOnOpenRef = useRef(false);
  const historyLoadPendingRef = useRef(false);
  const submitPromptRef = useRef(null);
  const isMountedRef = useRef(false);
  const isOpenRef = useRef(open);
  const userId = user?.id || "anon";

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    isOpenRef.current = open;
  }, [open]);

  const pendingComposerValueRef = useRef(null);
  const attachComposer = useCallback((composer) => {
    composerRef.current = composer;
    if (composer && pendingComposerValueRef.current !== null) {
      composer.setValue(pendingComposerValueRef.current);
      pendingComposerValueRef.current = null;
    }
  }, []);
  const getComposerValue = () => composerRef.current?.getValue?.() ?? pendingComposerValueRef.current ?? "";
  const setComposerValue = useCallback((nextValue = "") => {
    if (composerRef.current) composerRef.current.setValue(nextValue);
    else pendingComposerValueRef.current = nextValue;
  }, []);
  const clearComposerValue = useCallback(() => {
    setComposerValue("");
  }, [setComposerValue]);

  const displayName = useMemo(() => safeTrim(user?.name) || "there", [user?.name]);
  const navigateAfterJobSpyImport = useCallback(
    (payload = {}) => {
      const importedJobId = resolveImportedJobId(payload);
      if (!importedJobId) return false;

      navigate(`/jobs?jobId=${encodeURIComponent(String(importedJobId))}&tab=details`);
      return true;
    },
    [navigate]
  );
  const quickPrompts = useMemo(
    () => [
      "Show me all the last new jobs leads, of the company Hourglass...",
      "Show me, Hiring Managers contacts in New York...",
    ],
    []
  );

  useEffect(() => {
    if (!open) {
      attachmentPickerAutoOpenedRef.current = false;
      pendingSubmitOnOpenRef.current = false;
      return;
    }

    const token = getAuthToken();
    setAttachedFiles([]);
    clearComposerValue();
    if (safeTrim(initialPrompt)) {
      setComposerValue(initialPrompt);
    }
    pendingSubmitOnOpenRef.current = submitOnOpen ? safeTrim(initialPrompt) : "";
    setError("");
    setStatusMessage("");
    setShowHistory(false);
    setEditingEntryId("");
    setEditingValue("");
    setJobSpySelectedIdsByMessage({});
    setJobSpySaveStateByMessage({});

    if (!token) {
      setConversationItems([]);
      return;
    }

    let active = true;
    historyLoadPendingRef.current = true;
    setIsLoadingHistory(true);

    fetch(buildApiUrl("/api/ai-agent/messages"), {
      headers: {
        Authorization: `Bearer ${token}`,
        "x-access-token": token,
      },
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(safeTrim(payload?.error) || "Failed to load AI history.");
        }
        if (!active) return;
        setConversationItems(
          (Array.isArray(payload?.items) ? payload.items : []).map((item) =>
            normalizeMessageItem(item)
          )
        );
      })
      .catch((loadError) => {
        if (!active) return;
        console.error("Failed to load AI history:", loadError);
        setConversationItems([]);
        setError(loadError.message || "Failed to load AI history.");
      })
      .finally(() => {
        if (active) {
          historyLoadPendingRef.current = false;
          setIsLoadingHistory(false);
        }
      });

    return () => {
      active = false;
    };
  }, [clearComposerValue, initialPrompt, open, setComposerValue, submitOnOpen, userId]);

  useEffect(() => {
    if (!open) {
      attachmentPickerAutoOpenedRef.current = false;
      return;
    }

    if (!openAttachmentPickerOnOpen || attachmentPickerAutoOpenedRef.current) {
      return;
    }

    attachmentPickerAutoOpenedRef.current = true;
    const timeoutId = window.setTimeout(() => {
      fileInputRef.current?.click?.();
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [open, openAttachmentPickerOnOpen]);

  useEffect(() => {
    if (!open || historyLoadPendingRef.current || isLoadingHistory || isSubmitting || !pendingSubmitOnOpenRef.current) {
      return;
    }

    const prompt = pendingSubmitOnOpenRef.current;
    pendingSubmitOnOpenRef.current = false;
    if (!prompt) {
      return;
    }

    submitPromptRef.current?.("chat", prompt);
  }, [isLoadingHistory, isSubmitting, open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [conversationItems, isSubmitting, showHistory]);

  useEffect(() => {
    const voice = createAgentVoice({
      onListening: setIsListening, onBusy: setVoiceBusy, onError: setError,
      onText: text => {
        if (!isOpenRef.current || !isMountedRef.current) return;
        composerRef.current?.appendText?.(text);
        setStatusMessage("Voice captured. Review your message and press Send.");
      },
      transcribe: async (blob, signal) => {
        const data = new FormData();
        data.append("audio", blob, "recording");
        const token = getAuthToken();
        const response = await fetch(buildApiUrl("/api/ai-agent/transcribe"), {
          method: "POST", signal, headers: { Authorization: `Bearer ${token}`, "x-access-token": token }, body: data,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || "Unable to transcribe recording.");
        return payload.text;
      },
    });
    voiceRef.current = voice;
    const handleVisibility = () => { if (document.hidden) voice.cancel(); };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      voice.cancel(); voiceRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!open) voiceRef.current?.cancel();
  }, [open]);

  const persistConversation = (items) => {
    setConversationItems(items.map((item) => normalizeMessageItem(item)));
  };

  const replaceConversationItem = (nextItem) => {
    const normalizedItem = normalizeMessageItem(nextItem);
    setConversationItems((prev) =>
      prev.map((item) =>
        String(item.id) === String(normalizedItem.id) ? normalizedItem : item
      )
    );
  };

  const handleJobSpySelectionChange = (messageId, ids) => {
    const key = String(messageId);
    setJobSpySelectedIdsByMessage((prev) => ({
      ...prev,
      [key]: Array.isArray(ids) ? ids.map((value) => String(value)) : [],
    }));
  };

  const handleSaveJobSpyResults = async (messageId) => {
    const key = String(messageId);
    const selectedIds = Array.isArray(jobSpySelectedIdsByMessage[key])
      ? jobSpySelectedIdsByMessage[key]
      : [];
    if (!selectedIds.length) {
      setJobSpySaveStateByMessage((prev) => ({
        ...prev,
        [key]: { severity: "warning", text: "Select at least one search result before saving." },
      }));
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setError("Your session is missing. Please log in again.");
      return;
    }

    setJobSpySaveStateByMessage((prev) => ({
      ...prev,
      [key]: { severity: "info", text: "Saving selected search results...", saving: true },
    }));

    try {
      const response = await fetch(
        buildApiUrl(`/api/ai-agent/messages/${encodeURIComponent(messageId)}/jobspy-import`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "x-access-token": token,
          },
          body: JSON.stringify({ selected_ids: selectedIds }),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          safeTrim(payload?.error) || "Failed to import selected search results."
        );
      }

      if (payload?.item) {
        replaceConversationItem(payload.item);
      }

      setJobSpySelectedIdsByMessage((prev) => ({
        ...prev,
        [key]: [],
      }));
      setJobSpySaveStateByMessage((prev) => ({
        ...prev,
        [key]: {
          severity: "success",
          text:
            cleanJobSpySummaryText(payload?.summary_text) ||
            "Selected search results saved to the database.",
          saving: false,
        },
      }));
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent(JOBS_UPDATED_EVENT, {
            detail: {
              source: "ai-agent-jobspy-import",
              messageId: key,
              importedJobIds: Array.isArray(payload?.imported_job_ids) ? payload.imported_job_ids : [],
              importedCompanyIds: Array.isArray(payload?.imported_company_ids)
                ? payload.imported_company_ids
                : [],
            },
          })
        );
      }
      setStatusMessage("Selected search results saved to the database.");
      setError("");
      onActivity?.({
        type: "jobspy-save",
        createdAt: new Date().toISOString(),
        payload,
      });
      if (navigateAfterJobSpyImport(payload)) {
        onClose?.();
      }
    } catch (saveError) {
      console.error("Failed to save JobSpy rows:", saveError);
      setJobSpySaveStateByMessage((prev) => ({
        ...prev,
        [key]: {
          severity: "error",
          text: saveError.message || "Failed to save selected search results.",
          saving: false,
        },
      }));
    }
  };

  const clearConversation = async () => {
    const token = getAuthToken();
    if (!token) {
      setError("Your session is missing. Please log in again.");
      return;
    }

    try {
      const response = await fetch(buildApiUrl("/api/ai-agent/messages"), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "x-access-token": token,
        },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(safeTrim(payload?.error) || "Failed to clear AI history.");
      }

      voiceRef.current?.cancel();
      setMeetingDraft(null);
      setReviewMeeting(false);
      persistConversation([]);
      setShowHistory(false);
      setStatusMessage("Started a fresh AI thread.");
      setError("");
      setEditingEntryId("");
      setEditingValue("");
      setJobSpySelectedIdsByMessage({});
      setJobSpySaveStateByMessage({});
      onActivity?.({
        type: "clear",
        createdAt: new Date().toISOString(),
      });
    } catch (clearError) {
      console.error("Failed to clear AI history:", clearError);
      setError(clearError.message || "Failed to clear AI history.");
    }
  };

  const toggleMic = () => {
    if (isListening) voiceRef.current?.stop();
    else voiceRef.current?.start();
  };

  const handleAttachSelection = (event) => {
    const nextFiles = Array.from(event.target.files || []);
    event.target.value = "";
    if (!nextFiles.length) return;
    setAttachedFiles((prev) => [...prev, ...nextFiles].slice(0, MAX_ATTACHMENTS));
    setShowHistory(false);
    setError("");
    setStatusMessage("");
    if (!safeTrim(getComposerValue())) {
      setComposerValue(DEFAULT_ATTACHMENT_PROMPT);
    }
    onAttach?.(nextFiles.map((file) => ({ name: file.name, size: file.size, type: file.type })));
  };

  const removeAttachment = (nameToRemove) => {
    setAttachedFiles((prev) => prev.filter((file) => file.name !== nameToRemove));
  };

  const handleHistoryToggle = () => {
    setShowHistory((prev) => {
      const next = !prev;
      onHistory?.(next);
      return next;
    });
  };

  const handleCopyThread = async (item) => {
    const text = safeTrim(item?.text);
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setStatusMessage("Thread copied to clipboard.");
    } catch {
      setError("Unable to copy this thread.");
    }
  };

  const handleStartEdit = (item) => {
    setEditingEntryId(item.id);
    setEditingValue(item.text);
    setShowHistory(false);
    setStatusMessage("Editing thread locally. Save to update the visible conversation.");
    setError("");
  };

  const handleCancelEdit = () => {
    setEditingEntryId("");
    setEditingValue("");
    setStatusMessage("");
  };

  const handleSaveEdit = async (itemId) => {
    const nextText = safeTrim(editingValue);
    if (!nextText) {
      setError("Thread text cannot be empty.");
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setError("Your session is missing. Please log in again.");
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/api/ai-agent/messages/${encodeURIComponent(itemId)}`), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-access-token": token,
        },
        body: JSON.stringify({ text: nextText }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(safeTrim(payload?.error) || "Failed to update AI thread.");
      }

      const updatedItem = normalizeMessageItem(payload?.item || {});
      const updatedItems = conversationItems.map((item) =>
        String(item.id) === String(itemId) ? updatedItem : item
      );

      persistConversation(updatedItems);
      setEditingEntryId("");
      setEditingValue("");
      setStatusMessage("Thread updated in the database.");
      setError("");
      onActivity?.({
        type: "edit",
        createdAt: new Date().toISOString(),
        item: updatedItem,
      });
    } catch (updateError) {
      console.error("Failed to update AI thread:", updateError);
      setError(updateError.message || "Failed to update AI thread.");
    }
  };

  const handleDeleteThread = async (itemId) => {
    const token = getAuthToken();
    if (!token) {
      setError("Your session is missing. Please log in again.");
      return;
    }

    const targetItem = conversationItems.find((item) => String(item.id) === String(itemId));
    if (!targetItem) return;

    try {
      const response = await fetch(buildApiUrl(`/api/ai-agent/messages/${encodeURIComponent(itemId)}`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "x-access-token": token,
        },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(safeTrim(payload?.error) || "Failed to delete AI thread.");
      }

      const deletedIds = new Set(
        (Array.isArray(payload?.deleted_ids) ? payload.deleted_ids : []).map((value) =>
          String(value)
        )
      );
      const updatedItems = conversationItems.filter((item) => !deletedIds.has(String(item.id)));

      persistConversation(updatedItems);
      setJobSpySelectedIdsByMessage((prev) => {
        const next = { ...prev };
        deletedIds.forEach((id) => {
          delete next[String(id)];
        });
        return next;
      });
      setJobSpySaveStateByMessage((prev) => {
        const next = { ...prev };
        deletedIds.forEach((id) => {
          delete next[String(id)];
        });
        return next;
      });

      if (editingEntryId && deletedIds.has(String(editingEntryId))) {
        setEditingEntryId("");
        setEditingValue("");
      }

      setStatusMessage(
        targetItem?.role === "user" && deletedIds.size > 1
          ? "User thread and its AI reply deleted from the database."
          : "Thread deleted from the database."
      );
      setError("");
      onActivity?.({
        type: "delete",
        createdAt: new Date().toISOString(),
        deletedIds: Array.from(deletedIds),
      });
    } catch (deleteError) {
      console.error("Failed to delete AI thread:", deleteError);
      setError(deleteError.message || "Failed to delete AI thread.");
    }
  };

  const navigateToResultLink = (link) => {
    if (!link || link.external || !link.href) return;

    if (typeof window !== "undefined") {
      try {
        const parsedUrl = new URL(link.href, window.location.origin);
        const companyId = safeTrim(parsedUrl.searchParams.get("companyId"));
        const tab = safeTrim(parsedUrl.searchParams.get("tab")) || "details";
        const rawEntityType = safeTrim(parsedUrl.searchParams.get("entityType")).toLowerCase();
        const entityType =
          rawEntityType === "company"
            ? "company"
            : rawEntityType === "lead" || rawEntityType === "client"
              ? "lead"
              : "";

        if ((parsedUrl.pathname === "/companies" || parsedUrl.pathname === "/clients") && companyId && entityType) {
          navigate(`/companies${parsedUrl.search}`, {
            state: {
              viewMode: "leadTabsLayout",
              defaultTab: tab,
              returnListTab: entityType === "company" ? "companies" : "clients",
              openCompany: {
                id: companyId,
                name: link.label,
                entityType,
              },
            },
          });
          return;
        }
      } catch {
        // Fall back to the raw href when it is not a parsable internal app route.
      }
    }

    navigate(link.href);
  };

  const handleLinkedPillClick = (event, link) => {
    event.stopPropagation();
    if (link && !link.external && link.href) {
      event.preventDefault();
      navigateToResultLink(link);
    }
    onClose?.();
  };

  const handleSearchSubmit = () => submitPrompt("search");

  const submitPrompt = async (mode = "chat", suppliedPrompt) => {
    if (isSubmitting || voiceBusy || isListening) return;
    const prompt = safeTrim(suppliedPrompt ?? getComposerValue());
    if (!prompt && attachedFiles.length === 0) {
      setError("Enter a prompt or attach at least one file.");
      return;
    }
    const token = getAuthToken();
    if (!token) {
      setError("Your session is missing. Please log in again.");
      return;
    }
    setAttachedFiles([]);
    clearComposerValue();
    setShowHistory(false);
    setEditingEntryId("");
    setEditingValue("");
    setError("");
    setIsSubmitting(true);
    setStatusMessage("Searching...");
    const workStartedAt = new Date().toISOString();
    writeAiAgentWorkState(userId, {
      status: "working",
      mode,
      startedAt: workStartedAt,
      message: mode === "search" ? "AI Agent is searching for results..." : "AI Agent is working on your request...",
    });
    const filesToSend = [...attachedFiles];
    const formData = new FormData();
    formData.append("mode", mode);
    formData.append("time_zone", Intl.DateTimeFormat().resolvedOptions().timeZone);
    formData.append("message", prompt || "Analyze the attached files.");
    filesToSend.forEach((file) => formData.append("attachments", file));
    try {
      const response = await fetch(buildApiUrl("/api/ai-agent/respond"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "x-access-token": token,
        },
        body: formData,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(safeTrim(payload?.error) || `AI request failed with status ${response.status}.`);
      }
      if (isMountedRef.current && payload.meeting_draft) {
        setMeetingDraft(payload.meeting_draft);
        setReviewMeeting(isOpenRef.current);
      }
      const assistantItem = mergeAssistantPayloadWithResponse(payload, mode);
      const hasCreatedProposals = Array.isArray(payload.created_proposals) && payload.created_proposals.length > 0;
      if (hasCreatedProposals) window.dispatchEvent(new Event("rpo:content-updated"));
      writeAiAgentWorkState(userId, {
        status: "completed",
        mode,
        startedAt: workStartedAt,
        completedAt: new Date().toISOString(),
        message: mode === "search" ? "AI Agent finished the search." : "AI Agent finished your request.",
      });
      const nextItems = [
        ...conversationItems,
        normalizeMessageItem(payload?.user_message || {}),
        normalizeMessageItem(assistantItem),
      ].filter((item) => hasRenderableMessageContent(item));
      persistConversation(nextItems);
      setStatusMessage("");
      onActivity?.({
        type: mode === "search" ? "search" : "send",
        createdAt: new Date().toISOString(),
        payload,
      });
      if (mode === "search") onSearch?.(payload);
      else onSend?.(payload);

      // Auto-redirect to a newly created company when the AI response
      // returns exactly one internal company result link.
      const companyLinks = keepVisibleResultLinks(
        Array.isArray(assistantItem?.result_links) ? assistantItem.result_links : []
      ).filter((link) => link.kind === "company" && !link.external && link.href);
      if (!hasCreatedProposals && !payload.meeting_draft && companyLinks.length === 1 && isMountedRef.current && isOpenRef.current) {
        navigateToResultLink(companyLinks[0]);
        onClose?.();
      }
    } catch (requestError) {
      console.error("AI agent request failed:", requestError);
      writeAiAgentWorkState(userId, {
        status: "failed",
        mode,
        startedAt: workStartedAt,
        completedAt: new Date().toISOString(),
        message: requestError.message || "The AI Agent could not complete this request.",
      });
      setError(requestError.message || "Failed to contact the AI agent.");
      setComposerValue(prompt);
      setAttachedFiles(filesToSend);
    } finally {
      setIsSubmitting(false);
    }
  };
  submitPromptRef.current = submitPrompt;

  const historyItems = [...conversationItems].sort(
    (a, b) => toTimeValue(b.createdAt) - toTimeValue(a.createdAt)
  );

  return (
    <Dialog
      className="rpo-ai-agent-dialog"
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { background: "transparent", boxShadow: "none", overflow: "visible" } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          accept=".pdf,.txt,.md,.csv,.json,.doc,.docx,.rtf"
          onChange={handleAttachSelection}
        />
        <Box className="ai-agent-shell">
          <Box className="ai-agent-surface">
            <IconButton data-popup-close aria-label="Close AI Agent" onClick={onClose}>
              <CloseIcon sx={{ fontSize: 18, color: "#7C3AED" }} />
            </IconButton>
            <Stack className="ai-agent-layout" alignItems="center">
              <Box component="img" src={logoUrl} alt="AI" sx={{ width: 56, height: 56, objectFit: "contain" }} />
              <Typography data-popup-title sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 700, color: "#374151", textAlign: "center" }}>
                Hello, {displayName}.
              </Typography>
              <Box className="ai-agent-workspace" sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
                <Box className="ai-agent-composer" sx={{ order: 2, width: "100%" }}>
                  <Divider sx={{ mt: 2.2, mb: 2, borderColor: "rgba(124,58,237,0.14)" }} />
                  {voiceBusy && <Typography role="status">Processing voice input...</Typography>}
                  {isListening && <Typography role="status">Listening. Tap the microphone again when finished.</Typography>}
                  {meetingDraft && ["recruiter", "admin", "administrator"].includes(String(user?.role || "").toLowerCase()) && <Button onClick={() => setReviewMeeting(true)}>Review meeting</Button>}
                  <PromptComposer
                  ref={attachComposer}
                  autoFocus={open && !window.matchMedia?.("(pointer: coarse)").matches}
                  disabled={isSubmitting || voiceBusy}
                  isListening={isListening}
                  onSubmit={() => submitPrompt("chat")}
                  onToggleMic={toggleMic}
                />
                <Stack className="ai-agent-tools" direction="row" spacing={1.2} alignItems="center" sx={{ mt: 1.6, flexWrap: "wrap" }}>
                  <IconButton aria-label="Add attachment" onClick={() => fileInputRef.current?.click()} disabled={isSubmitting} sx={{ width: 38, height: 38, borderRadius: "999px", border: "1px solid rgba(124,58,237,0.35)", color: "#7C3AED", "&:hover": { bgcolor: "rgba(124,58,237,0.06)" } }}>
                    <AddIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <Button variant="outlined" onClick={() => fileInputRef.current?.click()} disabled={isSubmitting} startIcon={<AttachFileOutlinedIcon sx={{ fontSize: 18 }} />} sx={{ textTransform: "none", borderRadius: "999px", px: 2, borderColor: "rgba(124,58,237,0.35)", color: "#7C3AED" }}>
                    Attach
                  </Button>
                  <Button variant="outlined" onClick={handleSearchSubmit} disabled={isSubmitting} startIcon={<SearchOutlinedIcon sx={{ fontSize: 18 }} />} sx={{ textTransform: "none", borderRadius: "999px", px: 2, borderColor: "rgba(124,58,237,0.35)", color: "#7C3AED" }}>
                    Search
                  </Button>
                  <Button variant="outlined" onClick={handleHistoryToggle} startIcon={<HistoryOutlinedIcon sx={{ fontSize: 18 }} />} sx={{ textTransform: "none", borderRadius: "999px", px: 2, borderColor: showHistory ? "rgba(124,58,237,1)" : "rgba(124,58,237,0.35)", bgcolor: showHistory ? "rgba(124,58,237,0.08)" : "transparent", color: "#7C3AED" }}>
                    History
                  </Button>
                  {!!conversationItems.length && (
                    <Button variant="text" onClick={clearConversation} disabled={isSubmitting} sx={{ textTransform: "none", color: "#6B7280", minWidth: "auto", px: 0.5 }}>
                      New chat
                    </Button>
                  )}
                </Stack>
                {!!attachedFiles.length && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1.6, flexWrap: "wrap", gap: 1 }}>
                    {attachedFiles.map((file) => (
                      <Chip
                        key={`${file.name}-${file.size}`}
                        label={`${file.name}${formatFileSize(file.size) ? ` (${formatFileSize(file.size)})` : ""}`}
                        onDelete={() => removeAttachment(file.name)}
                        sx={{ borderRadius: "999px", bgcolor: "rgba(124,58,237,0.08)", color: "#5B21B6" }}
                      />
                    ))}
                  </Stack>
                )}
                {!!statusMessage && !isSubmitting && <Typography role="status" sx={{ mt: 1, fontSize: 13 }}>{statusMessage}</Typography>}
                {!!error && (
                  <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}
                </Box>
                <Box className="ai-agent-conversation" sx={{ order: 1, width: "100%" }}>
                  {isLoadingHistory ? (
                  <Box sx={{ borderRadius: "18px", px: 2, py: 2, bgcolor: "#F8FAFC", border: "1px solid rgba(148,163,184,0.18)" }}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <CircularProgress size={18} sx={{ color: "#7C3AED" }} />
                      <Typography sx={{ fontSize: 14, color: "#4B5563" }}>
                        Loading AI history...
                      </Typography>
                    </Stack>
                  </Box>
                ) : showHistory ? (
                  <Stack spacing={1.2}>
                    {historyItems.length ? (
                      historyItems.map((item) => {
                        const canReuse = item.role === "user";
                        const canManageThread = item.role === "user";
                        const isAssistant = item.role === "assistant";
                        const jobSpyMessageKey = String(item.id);
                        return (
                          <Box
                            key={item.id}
                            onClick={() => {
                              if (!canReuse) return;
                              setComposerValue(item.text);
                              setShowHistory(false);
                              setStatusMessage("Prompt restored from history.");
                            }}
                            sx={{
                              borderRadius: "16px",
                              border: "1px solid rgba(124,58,237,0.18)",
                              px: 2,
                              py: 1.4,
                              cursor: canReuse ? "pointer" : "default",
                              "&:hover": canReuse ? { bgcolor: "rgba(124,58,237,0.05)" } : undefined,
                            }}
                          >
                            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Chip size="small" label={item.role === "assistant" ? "AI" : "You"} sx={{ bgcolor: item.role === "assistant" ? "rgba(59,130,246,0.12)" : "rgba(124,58,237,0.12)", color: item.role === "assistant" ? "#1D4ED8" : "#7C3AED" }} />
                                <Chip size="small" label={item.mode === "search" ? "Web search" : "Chat"} variant="outlined" sx={{ borderColor: "rgba(124,58,237,0.25)" }} />
                              </Stack>
                              <Stack direction="row" spacing={0.25} alignItems="center">
                                <Typography sx={{ fontSize: 11, color: "#9CA3AF", mr: 0.75 }}>
                                  {formatTimestamp(item.createdAt)}
                                </Typography>
                                {canManageThread && (
                                  <>
                                    <IconButton size="small" onClick={(event) => { event.stopPropagation(); handleCopyThread(item); }} sx={{ color: "#94A3B8" }}>
                                      <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                    <IconButton size="small" onClick={(event) => { event.stopPropagation(); setShowHistory(false); handleStartEdit(item); }} sx={{ color: "#94A3B8" }}>
                                      <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                    <IconButton size="small" onClick={(event) => { event.stopPropagation(); handleDeleteThread(item.id); }} sx={{ color: "#94A3B8" }}>
                                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </>
                                )}
                              </Stack>
                            </Stack>
                            {!!safeTrim(item.text) && (
                              <Typography sx={{ fontSize: 13, color: "#4B5563", display: "-webkit-box", overflow: "hidden", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                                {item.text}
                              </Typography>
                            )}
                            {isAssistant && !!item.jobSpyResults?.length && (
                              <Box sx={{ mt: safeTrim(item.text) ? 1 : 0 }}>
                                <AIAgentJobSpyTable
                                  messageId={item.id}
                                  jobs={item.jobSpyResults}
                                  selectedIds={jobSpySelectedIdsByMessage[jobSpyMessageKey] || []}
                                  onSelectedIdsChange={(ids) =>
                                    handleJobSpySelectionChange(item.id, ids)
                                  }
                                  onSaveSelected={handleSaveJobSpyResults}
                                  isSaving={Boolean(jobSpySaveStateByMessage[jobSpyMessageKey]?.saving)}
                                  feedback={jobSpySaveStateByMessage[jobSpyMessageKey]}
                                />
                              </Box>
                            )}
                            {!item.jobSpyResults?.length && !!item.resultLinks?.length && (
                              <Stack direction="row" spacing={0.75} sx={{ mt: 0.9, flexWrap: "wrap", gap: 0.75 }}>
                                {item.resultLinks.slice(0, 3).map((link) => {
                                  const chipStyle = getResultLinkStyle(link);
                                  return (
                                    <Chip
                                      key={`${item.id}-${link.id}`}
                                      size="small"
                                      label={link.pageLabel ? `${link.pageLabel}: ${link.label}` : link.label}
                                      component={link.external ? "a" : RouterLink}
                                      href={link.external ? link.href : undefined}
                                      to={link.external ? undefined : link.href}
                                      target={link.external ? "_blank" : undefined}
                                      rel={link.external ? "noreferrer" : undefined}
                                      clickable
                                      icon={link.external ? <LaunchOutlinedIcon sx={{ fontSize: 14 }} /> : undefined}
                                      variant="outlined"
                                      onClick={(e) => handleLinkedPillClick(e, link)}
                                      sx={{
                                        borderRadius: "999px",
                                        ...chipStyle,
                                        "& .MuiChip-label": { maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" },
                                      }}
                                    />
                                  );
                                })}
                              </Stack>
                            )}
                          </Box>
                        );
                      })
                    ) : (
                      <Box sx={{ borderRadius: "18px", border: "1px dashed rgba(124,58,237,0.22)", px: 2, py: 2.4 }}>
                        <Typography sx={{ color: "#6B7280", fontSize: 14 }}>
                          Your AI Agent history will show up here after the first message.
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                ) : conversationItems.length ? (
                  <Stack spacing={1.2} sx={{ maxHeight: 420, overflowY: "auto", pr: 0.5 }}>
                    {conversationItems.map((item) => {
                      const isAssistant = item.role === "assistant";
                      const canManageThread = item.role === "user";
                      const jobSpyMessageKey = String(item.id);
                      return (
                        <Box
                          key={item.id}
                          sx={{
                            alignSelf: isAssistant ? "stretch" : "flex-end",
                            borderRadius: "18px",
                            px: 2,
                            py: 1.4,
                            bgcolor: isAssistant ? "#F8FAFC" : "rgba(124,58,237,0.08)",
                            border: `1px solid ${isAssistant ? "rgba(148,163,184,0.18)" : "rgba(124,58,237,0.18)"}`,
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: isAssistant ? "#1F2937" : "#6D28D9" }}>
                                {isAssistant ? "RPO AI" : "You"}
                              </Typography>
                              <Chip size="small" label={item.mode === "search" ? "Web search" : "Chat"} variant="outlined" sx={{ borderColor: "rgba(124,58,237,0.25)" }} />
                            </Stack>
                            <Stack direction="row" spacing={0.25} alignItems="center">
                              <Typography sx={{ fontSize: 11, color: "#9CA3AF", mr: 0.75 }}>
                                {formatTimestamp(item.createdAt)}
                              </Typography>
                              {canManageThread && (
                                <>
                                  <IconButton size="small" onClick={() => handleCopyThread(item)} sx={{ color: "#94A3B8" }}>
                                    <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  <IconButton size="small" onClick={() => handleStartEdit(item)} sx={{ color: "#94A3B8" }}>
                                    <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  <IconButton size="small" onClick={() => handleDeleteThread(item.id)} sx={{ color: "#94A3B8" }}>
                                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </>
                              )}
                            </Stack>
                          </Stack>
                          {editingEntryId === item.id ? (
                            <Stack spacing={1}>
                              <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                value={editingValue}
                                onChange={(event) => setEditingValue(event.target.value)}
                                sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#fff" } }}
                              />
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button size="small" variant="outlined" onClick={handleCancelEdit} sx={{ textTransform: "none" }}>
                                  Cancel
                                </Button>
                                <Button size="small" variant="contained" onClick={() => handleSaveEdit(item.id)} startIcon={<CheckRoundedIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: "none", background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}>
                                  Save
                                </Button>
                              </Stack>
                            </Stack>
                          ) : (
                            <Typography sx={{ fontSize: 14, color: "#374151", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                              {item.text}
                            </Typography>
                          )}
                          {isAssistant && !!item.jobSpyResults?.length && (
                            <Box sx={{ mt: safeTrim(item.text) ? 1.1 : 0 }}>
                              <AIAgentJobSpyTable
                                messageId={item.id}
                                jobs={item.jobSpyResults}
                                selectedIds={jobSpySelectedIdsByMessage[jobSpyMessageKey] || []}
                                onSelectedIdsChange={(ids) =>
                                  handleJobSpySelectionChange(item.id, ids)
                                }
                                onSaveSelected={handleSaveJobSpyResults}
                                isSaving={Boolean(jobSpySaveStateByMessage[jobSpyMessageKey]?.saving)}
                                feedback={jobSpySaveStateByMessage[jobSpyMessageKey]}
                              />
                            </Box>
                          )}
                          {!!item.attachments?.length && (
                            <Stack direction="row" spacing={1} sx={{ mt: 1.1, flexWrap: "wrap", gap: 1 }}>
                              {item.attachments.map((file) => (
                                <Chip key={`${item.id}-${file.name}`} size="small" label={`${file.name}${formatFileSize(file.size) ? ` (${formatFileSize(file.size)})` : ""}`} sx={{ bgcolor: "rgba(124,58,237,0.08)", color: "#5B21B6" }} />
                              ))}
                            </Stack>
                          )}
                          {!item.jobSpyResults?.length && !!item.resultLinks?.length && (
                            <Stack spacing={0.65} sx={{ mt: 1.1 }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6B7280" }}>
                                Results
                              </Typography>
                              <Stack direction="row" spacing={0.85} sx={{ flexWrap: "wrap", gap: 0.85 }}>
                                {item.resultLinks.map((link) => {
                                  const chipStyle = getResultLinkStyle(link);
                                  return (
                                    <Chip
                                      key={`${item.id}-${link.id}`}
                                      size="small"
                                      label={link.pageLabel ? `${link.pageLabel}: ${link.label}` : link.label}
                                      component={link.external ? "a" : RouterLink}
                                      href={link.external ? link.href : undefined}
                                      to={link.external ? undefined : link.href}
                                      target={link.external ? "_blank" : undefined}
                                      rel={link.external ? "noreferrer" : undefined}
                                      clickable
                                      icon={link.external ? <LaunchOutlinedIcon sx={{ fontSize: 14 }} /> : undefined}
                                      variant="outlined"
                                      onClick={(e) => handleLinkedPillClick(e, link)}
                                      sx={{
                                        borderRadius: "999px",
                                        ...chipStyle,
                                        "& .MuiChip-label": {
                                          maxWidth: { xs: 220, sm: 320 },
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        },
                                      }}
                                    />
                                  );
                                })}
                              </Stack>
                            </Stack>
                          )}
                          {!!item.citations?.length && (
                            <Stack spacing={0.5} sx={{ mt: 1.1 }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6B7280" }}>
                                External links
                              </Typography>
                              <Stack direction="row" spacing={0.85} sx={{ flexWrap: "wrap", gap: 0.85 }}>
                                {item.citations.map((citation, index) => (
                                  <Chip
                                    key={`${item.id}-${citation.url || index}`}
                                    size="small"
                                    component="a"
                                    href={citation.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    clickable
                                    icon={<LaunchOutlinedIcon sx={{ fontSize: 14 }} />}
                                    label={safeTrim(citation.title) || safeTrim(citation.url) || `Source ${index + 1}`}
                                    variant="outlined"
                                    onClick={(e) => handleLinkedPillClick(e, null)}
                                    sx={{
                                      borderRadius: "999px",
                                      ...RESULT_LINK_STYLES.external,
                                      "& .MuiChip-label": {
                                        maxWidth: { xs: 220, sm: 320 },
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      },
                                    }}
                                  />
                                ))}
                              </Stack>
                            </Stack>
                          )}
                        </Box>
                      );
                    })}
                    {isSubmitting && (
                      <Box sx={{ borderRadius: "18px", px: 2, py: 1.4, bgcolor: "#F8FAFC", border: "1px solid rgba(148,163,184,0.18)" }}>
                        <Stack direction="row" spacing={1.2} alignItems="center">
                          <CircularProgress size={18} sx={{ color: "#7C3AED" }} />
                          <Typography sx={{ fontSize: 14, color: "#4B5563" }}>
                            {statusMessage || "Working on it..."}
                          </Typography>
                        </Stack>
                      </Box>
                    )}
                    <Box ref={endRef} />
                  </Stack>
                ) : (
                  <Stack spacing={1.2}>
                    {quickPrompts.map((text) => (
                      <Box key={text} onClick={() => { setComposerValue(text); setShowHistory(false); setError(""); setStatusMessage(""); }} sx={{ cursor: "pointer", userSelect: "none", borderRadius: "999px", border: "1px solid rgba(124,58,237,0.35)", px: 2, py: 1.1, display: "flex", alignItems: "center", gap: 1, "&:hover": { bgcolor: "rgba(124,58,237,0.06)" } }}>
                        <BoltOutlinedIcon sx={{ color: "#7C3AED", fontSize: 18 }} />
                        <Typography sx={{ fontSize: 14, color: "#4B5563", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {text}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
                </Box>
              </Box>
            </Stack>
          </Box>
        </Box>
        <CandidateScheduleDialog open={open && reviewMeeting && !!meetingDraft} initialDraft={meetingDraft}
          onClose={() => setReviewMeeting(false)} onSaved={() => { setMeetingDraft(null); setReviewMeeting(false); setStatusMessage("Meeting scheduled."); }} />
      </DialogContent>
    </Dialog>
  );
}
